import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import disputeAdminService from '../../services/disputeAdminService';

// URL de base du backend pour les fichiers statiques
const BACKEND_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

// Helper pour construire l'URL complète d'un fichier
const getFileUrl = (path) => {
  if (!path) return '';
  // Si l'URL est déjà absolue (http:// ou https://), la retourner telle quelle
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Sinon, ajouter l'URL du backend
  return `${BACKEND_URL}${path}`;
};

const PrivateChannelMessage = ({ event, currentUserId }) => {
  const { t } = useTranslation('disputes');
  // Le message est "le mien" si je suis l'auteur
  const isOwnMessage = event.actor_id === currentUserId || event.actor?.id === currentUserId;
  // L'auteur est un admin si son rôle est admin (stocké dans metadata ou actor)
  const isFromAdmin = event.metadata?.from_admin === true || event.actor?.role === 'admin';
  
  // Pièces jointes de l'événement
  const attachments = event.eventAttachments || event.attachments || [];
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
        mb: 1.5
      }}
    >
      <Box
        sx={{
          maxWidth: '80%',
          bgcolor: isOwnMessage ? 'primary.main' : (isFromAdmin ? 'warning.light' : 'grey.200'),
          color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
          p: 1.5,
          borderRadius: 2,
          borderTopRightRadius: isOwnMessage ? 0 : 2,
          borderTopLeftRadius: isOwnMessage ? 2 : 0
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: isFromAdmin ? 'warning.dark' : (isOwnMessage ? 'primary.dark' : 'grey.500') }}>
            {isFromAdmin ? <AdminPanelSettingsIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
          </Avatar>
          <Typography variant="caption" fontWeight="bold">
            {isOwnMessage ? t('common.you', 'Vous') : (isFromAdmin ? t('common.administrator', 'Administrateur') : (event.actor?.name || t('common.user', 'Utilisateur')))}
          </Typography>
          <Chip 
            icon={<LockIcon sx={{ fontSize: 12 }} />} 
            label={t('common.private', 'Privé')} 
            size="small" 
            color="warning"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        </Stack>
        {event.message && (
          <Typography variant="body2">
            {event.message}
          </Typography>
        )}
        {/* Affichage des pièces jointes */}
        {attachments.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {attachments.map((attachment, index) => {
              const isImage = attachment.file_type?.startsWith('image/');
              const fileName = attachment.metadata?.original_name || 'Fichier';
              return (
                <Box 
                  key={index}
                  component="a"
                  href={getFileUrl(attachment.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: isOwnMessage ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { bgcolor: isOwnMessage ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)' }
                  }}
                >
                  {isImage ? (
                    attachment.thumbnail_url ? (
                      <Box
                        component="img"
                        src={getFileUrl(attachment.thumbnail_url)}
                        alt={fileName}
                        sx={{ width: 40, height: 40, borderRadius: 0.5, objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 20 }} />
                    )
                  ) : (
                    <InsertDriveFileIcon sx={{ fontSize: 20 }} />
                  )}
                  <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fileName.length > 25 ? `${fileName.substring(0, 22)}...` : fileName}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'right', opacity: 0.7 }}>
          {format(new Date(event.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
        </Typography>
      </Box>
    </Box>
  );
};

const PrivateChannelsPanel = ({ dispute, currentUser, onMessageSent }) => {
  const { t } = useTranslation('disputes');
  const [activeTab, setActiveTab] = useState(0);
  const [ownerMessages, setOwnerMessages] = useState([]);
  const [renterMessages, setRenterMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Déterminer les IDs des parties depuis la réservation
  const ownerId = dispute.reservation?.listing?.owner?.id || dispute.reservation?.listing?.ownerId || dispute.metadata?.owner_id;
  const renterId = dispute.reservation?.renter?.id || dispute.reservation?.renter_id || dispute.metadata?.renter_id;
  
  // Déterminer le rôle de l'utilisateur courant
  // Note: user.roles est un tableau, pas user.role
  const isAdmin = Array.isArray(currentUser?.roles) && currentUser.roles.includes('admin');
  const isOwner = !isAdmin && currentUser?.id === ownerId;
  const isRenter = !isAdmin && currentUser?.id === renterId;
  
  // Debug pour comprendre les rôles
  console.log('PrivateChannelsPanel roles:', { 
    userId: currentUser?.id, 
    userRole: currentUser?.role,
    userRoles: currentUser?.roles,
    ownerId, 
    renterId, 
    isAdmin, 
    isOwner, 
    isRenter 
  });

  // Charger les messages privés
  const loadMessages = async () => {
    if (!dispute?.dispute_id && !dispute?.id) return;
    
    setLoading(true);
    try {
      const disputeId = dispute.dispute_id || dispute.id;
      
      if (isAdmin) {
        // Admin charge les deux canaux
        const ownerEvents = await disputeAdminService.getFilteredEvents(disputeId, 'owner_only');
        const renterEvents = await disputeAdminService.getFilteredEvents(disputeId, 'renter_only');
        setOwnerMessages(ownerEvents?.data || []);
        setRenterMessages(renterEvents?.data || []);
      } else if (isOwner) {
        const events = await disputeAdminService.getFilteredEvents(disputeId, 'owner_only');
        setOwnerMessages(events?.data || []);
      } else if (isRenter) {
        const events = await disputeAdminService.getFilteredEvents(disputeId, 'renter_only');
        setRenterMessages(events?.data || []);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispute?.dispute_id, dispute?.id]);

  useEffect(() => {
    // Scroll vers le bas quand les messages changent
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ownerMessages, renterMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    setSendingMessage(true);
    setError(null);

    try {
      // Déterminer la visibilité selon le rôle
      let visibility;
      if (isAdmin) {
        // Admin envoie dans le canal sélectionné via l'onglet
        visibility = activeTab === 0 ? 'owner_only' : 'renter_only';
      } else if (isOwner) {
        // Owner envoie toujours dans owner_only
        visibility = 'owner_only';
      } else if (isRenter) {
        // Renter envoie toujours dans renter_only
        visibility = 'renter_only';
      } else {
        throw new Error('Vous n\'avez pas accès à ce canal');
      }
      
      const disputeId = dispute.dispute_id || dispute.id;
      
      // Utiliser FormData si on a des pièces jointes
      if (attachments.length > 0) {
        const formData = new FormData();
        if (newMessage.trim()) {
          formData.append('message', newMessage.trim());
        }
        formData.append('visibility', visibility);
        attachments.forEach((file) => {
          formData.append('attachments', file);
        });
        await disputeAdminService.sendPrivateMessage(disputeId, formData);
      } else {
        await disputeAdminService.sendPrivateMessage(disputeId, {
          message: newMessage.trim(),
          visibility
        });
      }

      setNewMessage('');
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadMessages();
      onMessageSent?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Ne pas afficher si le litige n'est pas escaladé
  if (dispute.status !== 'escalated') {
    return null;
  }

  // Déterminer quel canal afficher selon le rôle
  const renderMessages = () => {
    const messages = isAdmin 
      ? (activeTab === 0 ? ownerMessages : renterMessages)
      : (isOwner ? ownerMessages : renterMessages);

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (messages.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
          <LockIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            {t('privateChannel.noMessages', 'Aucun message dans ce canal privé')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxHeight: 300, overflowY: 'auto', p: 2 }}>
        {messages.map((event) => (
          <PrivateChannelMessage key={event.id} event={event} currentUserId={currentUser?.id} />
        ))}
        <div ref={messagesEndRef} />
      </Box>
    );
  };

  // Déterminer les noms des parties
  // Le owner est soit dans listing.owner, soit on déduit depuis reporter/reportedUser selon qui est le renter
  const getOwnerName = () => {
    // Si listing.owner existe directement
    if (dispute.reservation?.listing?.owner?.name) {
      return dispute.reservation.listing.owner.name;
    }
    // Sinon, déduire : si le renter est le reporter, alors le owner est reportedUser et vice-versa
    if (dispute.reservation?.renter_id === dispute.reporter?.id) {
      return dispute.reportedUser?.name || dispute.reportedUser?.email || t('common.owner', 'Propriétaire');
    }
    // Sinon le reporter est probablement le owner
    return dispute.reporter?.name || dispute.reporter?.email || t('common.owner', 'Propriétaire');
  };
  
  const getRenterName = () => {
    // Si renter existe directement
    if (dispute.reservation?.renter?.name) {
      return dispute.reservation.renter.name;
    }
    // Sinon, déduire : si le reporter est le renter_id
    if (dispute.reservation?.renter_id === dispute.reporter?.id) {
      return dispute.reporter?.name || dispute.reporter?.email || t('common.renter', 'Locataire');
    }
    // Sinon le reportedUser est probablement le renter
    return dispute.reportedUser?.name || dispute.reportedUser?.email || t('common.renter', 'Locataire');
  };

  const ownerName = getOwnerName();
  const renterName = getRenterName();

  return (
    <Paper elevation={2} sx={{ mb: 2, overflow: 'hidden', border: '1px solid', borderColor: 'warning.main' }}>
      <Box sx={{ bgcolor: 'warning.light', px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LockIcon color="warning" />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('privateChannel.title', 'Communication privée')}
          </Typography>
          {isAdmin && <Chip label="Admin" size="small" color="primary" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {isAdmin 
            ? t('privateChannel.adminDescription', 'Communiquez confidentiellement avec chaque partie. Vos échanges ne sont pas visibles par l\'autre partie.')
            : t('privateChannel.userDescription', 'Canal de communication confidentiel avec l\'administrateur.')
          }
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {isAdmin ? (
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<HomeIcon />} 
            iconPosition="start" 
            label={ownerName}
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start" 
            label={renterName}
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      ) : (
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AdminPanelSettingsIcon color="primary" />
            <Typography variant="subtitle2">
              {t('privateChannel.adminChannel', 'Canal avec l\'administrateur')}
            </Typography>
          </Stack>
        </Box>
      )}

      {renderMessages()}

      <Divider />

      {/* Zone de saisie de message */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        {/* Affichage des fichiers sélectionnés */}
        {attachments.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {attachments.map((file, index) => (
              <Chip
                key={index}
                icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                label={file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                size="small"
                onDelete={() => handleRemoveAttachment(index)}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: 'white' }}
              />
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <IconButton 
            component="label"
            size="small" 
            disabled={sendingMessage}
            color="primary"
          >
            <AttachFileIcon />
            <input
              type="file"
              hidden
              multiple
              accept="*/*"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </IconButton>
          <TextField
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              isAdmin 
                ? (activeTab === 0 
                    ? t('privateChannel.adminToOwnerPlaceholder', 'Message privé au propriétaire...')
                    : t('privateChannel.adminToRenterPlaceholder', 'Message privé au locataire...'))
                : t('privateChannel.userPlaceholder', 'Répondre à l\'administrateur...')
            }
            fullWidth
            size="small"
            disabled={sendingMessage}
            sx={{ bgcolor: 'white' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            disabled={sendingMessage || (!newMessage.trim() && attachments.length === 0)}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </Button>
        </Stack>
        {isAdmin && (
          <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
            <LockIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
            {activeTab === 0 
              ? t('privateChannel.adminOwnerNote', 'Ce message sera visible uniquement par vous et le propriétaire.')
              : t('privateChannel.adminRenterNote', 'Ce message sera visible uniquement par vous et le locataire.')
            }
          </Typography>
        )}
        {!isAdmin && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            <LockIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
            {t('privateChannel.privacyNote', 'Ce message ne sera visible que par vous et l\'administrateur.')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default PrivateChannelsPanel;
